/**
 * Blog Page
 * Data-driven blog listing with SEO optimization
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs, { createBreadcrumbs } from '../components/Breadcrumbs';
import { RelatedLocations, ContextualCTA } from '../components/InternalLinks';
import Footer from '../components/Footer';
import BookAppointmentLink from '../components/BookAppointmentLink';
import { blogPosts, pagesMeta } from '../data/seo';

const Blog: React.FC = () => {
  const meta = pagesMeta.blog;
  const breadcrumbs = createBreadcrumbs.page('Blog', '/blog');
  
  // Group posts by category for better organization
  const categories = Array.from(new Set(blogPosts.map(p => p.category)));
  const featuredPosts = blogPosts.filter(p => p.featured);
  
  return (
    <>
      <SEO 
        meta={meta}
        breadcrumbs={breadcrumbs}
      />

      <div className="min-h-screen bg-gradient-to-br from-soft-gray to-white">
        {/* Header */}
        <section className="bg-brand-teal text-white py-16">
          <div className="container-custom">
            <Breadcrumbs items={breadcrumbs} className="mb-6 text-white/70" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-nantes">
              Pet Dental Health Blog
            </h1>
            <p className="text-xl md:text-2xl opacity-90 max-w-2xl">
              Expert advice and insights from South Florida's dedicated pet dental specialists
            </p>
          </div>
        </section>

        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <section className="section-padding bg-white">
            <div className="container-custom">
              <h2 className="text-2xl font-bold text-primary mb-8">
                <i className="fas fa-star text-yellow-500 mr-2"></i>
                Featured Articles
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredPosts.map((post) => (
                  <article
                    key={post.slug}
                    className="bg-gradient-to-br from-brand-teal/5 to-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col border-2 border-brand-teal/20"
                  >
                    <div className="h-48 overflow-hidden relative">
                      {post.image ? (
                        <img 
                          src={post.image} 
                          alt={post.imageAlt || post.title}
                          loading="lazy"
                          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-brand-teal/20 to-brand-teal/5 flex items-center justify-center">
                          <i className="fas fa-tooth text-brand-teal text-4xl opacity-50"></i>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                          <i className="fas fa-star mr-1"></i>Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                        <span className="text-gray-400 text-sm">{post.date}</span>
                      </div>
                      <h3 className="text-xl font-bold text-primary mb-3 leading-tight hover:text-brand-teal transition-colors">
                        <Link to={`/blog/${post.slug}`}>
                          {post.title}
                        </Link>
                      </h3>
                      <p className="text-gray-600 mb-4 flex-grow line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">
                          <i className="fas fa-clock mr-1"></i>
                          {post.readTime}
                        </span>
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-brand-teal font-semibold hover:underline"
                        >
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Posts by Category */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <h2 className="text-2xl font-bold text-primary mb-8">All Articles</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {blogPosts.map((post) => (
                    <article
                      key={post.slug}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col"
                    >
                      <div className="h-40 overflow-hidden relative">
                        {post.image ? (
                          <img 
                            src={post.image} 
                            alt={post.imageAlt || post.title}
                            loading="lazy"
                            className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-brand-teal/20 to-brand-teal/5 flex items-center justify-center">
                            <i className="fas fa-tooth text-brand-teal text-3xl opacity-50"></i>
                          </div>
                        )}
                      </div>
                      <div className="p-5 flex flex-col flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-brand-teal/10 text-brand-teal px-2 py-0.5 rounded-full text-xs font-semibold">
                            {post.category}
                          </span>
                          <span className="text-gray-400 text-xs">{post.date}</span>
                        </div>
                        <h3 className="text-lg font-bold text-primary mb-2 leading-tight hover:text-brand-teal transition-colors line-clamp-2">
                          <Link to={`/blog/${post.slug}`}>
                            {post.title}
                          </Link>
                        </h3>
                        <p className="text-gray-600 text-sm mb-3 flex-grow line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                          <span className="text-xs text-gray-500">
                            <i className="fas fa-clock mr-1"></i>
                            {post.readTime}
                          </span>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="text-brand-teal font-semibold text-sm hover:underline"
                          >
                            Read →
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-8">
                {/* Categories */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="text-lg font-bold text-primary mb-4">Categories</h3>
                  <ul className="space-y-2">
                    {categories.map((category) => (
                      <li key={category}>
                        <span className="flex items-center justify-between text-gray-700 hover:text-brand-teal transition-colors cursor-pointer">
                          <span>{category}</span>
                          <span className="text-xs text-gray-400">
                            ({blogPosts.filter(p => p.category === category).length})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Locations Served */}
                <RelatedLocations 
                  title="Find Pet Dental Care Near You" 
                  limit={6}
                />

                {/* Quick Contact */}
                <div className="bg-brand-teal text-white rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-3">Have Questions?</h3>
                  <p className="opacity-90 text-sm mb-4">
                    Our team is here to help with your pet's dental health.
                  </p>
                  <BookAppointmentLink
                    className="block w-full bg-white text-brand-teal py-3 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors"
                  >
                    Book Free Consultation
                  </BookAppointmentLink>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-white">
          <div className="container-custom max-w-4xl">
            <ContextualCTA variant="box" />
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
